import React from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { Button } from "../../";
import { FormInput } from "../../forms";
import { usePatchDocument } from "./hooks/usePatchDocument";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Controller, useForm } from "react-hook-form";
import { DocumentDetailsCard } from "../../utilities/DocumentDetailsCard";
import { DocumentEditModalProps } from "./types";
import { DocumentType } from "../../../types/DocumentType";
import { BooleanSelect, DocumentFunctionsSelect } from "../../forms/selects"; 
import { ClientProjectSearchSelect } from "../../forms/selects/ClientProjectSearchSelect";
import ThemedModal from "../../utilities/ThemedModal";

const DialogBody = styled(Modal.Body)``;

export const DocumentEditModal = ({
    adviserId,
    divisionId,
    backdrop = 'static',
    cancel,
    centered = true,
    data,
    defaultValues,
    dialogId,
    dismiss,
    proceed,
    show,
    size = "lg",
    userId,
    ...rest
} : Readonly<DocumentEditModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const { patchDocument, isLoading: isUpdating } = usePatchDocument();
    const { control, formState: { dirtyFields, errors }, getValues, handleSubmit, reset, setValue, watch } = useForm<DocumentType>();
    const masterAccountId = watch('masterAccountId');

    const onClose = () => cancel!();

    const onSubmit = (form: DocumentType) => {
        // check there is actual data/changed fields we can submit
        if (!dirtyFields || Object.keys(dirtyFields).length === 0) {
            onClose();
        }

        const keys = Object.keys(dirtyFields) as Array<keyof DocumentType>;
        const operations : JsonPatchOperation[] = keys.map((el) => ({
            op: 'replace',
            path: `/${el}`,
            value: getValues(el)
        }));

        if (!operations || operations.length === 0) {
            onClose();
        }

        patchDocument({ id: form.documentId, operations }).then((r) => proceed!(r));
    };
    
    React.useEffect(() => {
        if (!data) {
            return;
        }
        reset({ ...data });
    }, [ data, reset ])

    return (
        <Modal className="overflow-visible" backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header closeButton>
                <Modal.Title>Edit: {data.fileName}</Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody ref={bodyAnimation}>
                    <DocumentDetailsCard 
                        type={data.documentType}
                        fileName={data.fileName}
                        fileSize={data.fileSize}
                    />
                    {masterAccountId && divisionId && adviserId && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <Controller
                                    name="projectId"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <ClientProjectSearchSelect
                                            isDisabled={isUpdating}
                                            adviserId={adviserId}
                                            divisionId={divisionId}
                                            masterAccountId={masterAccountId!}
                                            userId={userId}
                                            value={value}
                                            onChange={(e: any) => {
                                                if (e === null) {
                                                    setValue('projectId', null, { shouldDirty: true });
                                                    return;
                                                }
                                                setValue('projectId', e.value, { shouldDirty: true })
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <div className="row mb-3">
                        <div className="col">
                            <Controller
                                name="documentDescription"
                                control={control}
                                rules={{ 
                                    maxLength: {
                                        value: 2048,
                                        message: 'Document description cannot be more than 2048 characters.'
                                    } 
                                }}
                                render={({ field: { ref, ...rest } }) => (
                                    <FormInput 
                                        label="Document Description"
                                        disabled={isUpdating} 
                                        disableAnimations={true} 
                                        errorAllowRetry={false}
                                        errorMessage={errors?.documentDescription?.message}
                                        {...rest} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col">
                            <Controller
                                name="documentFunctionId"
                                control={control}
                                rules={{ required: "A Document Function must be selected." }}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <DocumentFunctionsSelect
                                        label="Document Function"
                                        defaultValue={value}
                                        isDisabled={isUpdating}
                                        disableAnimations={true}
                                        errorMessage={errors?.documentFunctionId?.message} 
                                        errorAllowRetry={false}
                                        onChange={(values) => setValue('documentFunctionId', values.value, { shouldDirty: true })}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        {masterAccountId && (
                            <div className="col">
                                <Controller
                                    name="clientReadable"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <BooleanSelect
                                            label="Client Visible"
                                            defaultValue={value}
                                            isDisabled={isUpdating}
                                            disableAnimations={true}
                                            onChange={(values: any) => {
                                                setValue('clientReadable', values.value, { shouldDirty: true })
                                            }}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </DialogBody>
                <Modal.Footer>
                    <Button type="submit" variant="success" disabled={isUpdating}>Save Changes</Button>
                    <Button variant="light" onClick={onClose} disabled={isUpdating}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};